import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc,
  where,
  query,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { Visit, Event } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

const visitCollectionRef = collection(db, "visits");
const eventCollectionRef = collection(db, "events");

export const VisitService = {
  async createVisit(
    visitData: Omit<Visit, "id" | "createdAt" | "updatedAt" | "systemBilled">,
  ): Promise<string> {
    const newVisit: Omit<Visit, "id"> = {
      ...visitData,
      systemBilled: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const visitRef = await addDoc(visitCollectionRef, clean(newVisit));
    return visitRef.id;
  },

  async getVisit(visitId: string): Promise<Visit | null> {
    const visitDoc = await getDoc(doc(visitCollectionRef, visitId));
    if (visitDoc.exists()) {
      return { id: visitDoc.id, ...visitDoc.data() } as Visit;
    }
    return null;
  },

  async getVisitsForDateRange(
    accountId: string,
    start: Date,
    end: Date,
  ): Promise<Visit[]> {
    const visitSnapshot = await getDocs(
      query(
        visitCollectionRef,
        where("accountId", "==", accountId),
        where("startDate", ">=", Timestamp.fromDate(start)),
        where("endDate", "<=", Timestamp.fromDate(end)),
        orderBy("createdAt", "asc"),
      ),
    );
    return visitSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Visit,
    );
  },

  async getUpcomingVisits(accountId: string): Promise<Visit[]> {
    const visitSnapshot = await getDocs(
      query(
        visitCollectionRef,
        where("accountId", "==", accountId),
        where("startDate", ">=", Timestamp.now()),
        orderBy("startDate", "asc"),
      ),
    );
    return visitSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Visit,
    );
  },

  async getAllVisits(accountId: string): Promise<Visit[]> {
    const visitSnapshot = await getDocs(
      query(
        visitCollectionRef,
        where("accountId", "==", accountId),
        orderBy("startDate", "desc"),
      ),
    );
    return visitSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Visit,
    );
  },

  async getVisitEvents(visitId: string): Promise<Event[]> {
    const visitSnapshot = await getDocs(
      query(
        eventCollectionRef,
        where("visitId", "==", visitId),
        orderBy("createdAt", "desc"),
      ),
    );
    return visitSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Event,
    );
  },

  async updateVisit(
    visitId: string,
    updatedData: Partial<Omit<Visit, "id" | "createdAt">>,
  ): Promise<void> {
    const visitDocRef = doc(visitCollectionRef, visitId);
    await setDoc(
      visitDocRef,
      clean({
        ...updatedData,
        updatedAt: Timestamp.now(),
      }),
      { merge: true },
    );
  },

  async deleteVisit(visitId: string): Promise<void> {
    const visitDocRef = doc(visitCollectionRef, visitId);
    await deleteDoc(visitDocRef);
  },
};
